/* eslint-disable max-len */
import { StatusCode } from '@/api/enumerations';
import { createDelegatedSession } from '@/api/oversight';
import { IconCloseMS } from '@/constants/icons';
import useErrorMessage from '@/hooks/useErrorMessage';
import useSnackbar from '@/hooks/useSnackbar';
import { Button } from '@/pages/Oversight/components/Button';
import { Info } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useState } from 'react';

import {
  Caption,
  ClientTextField,
  CloseButton,
  ContainerButtons,
  ContainerInfo,
  DialogContainer,
  InfoItem,
  ReasonBox,
  ReasonTextField,
  StyledDialog,
  Title,
  TopContainer,
  WarningMessage,
} from '../styles';

type DialogProps = {
  open: boolean;
  title?: string;
  handleClose: () => void;
  clientId?: number;
  clientName?: string;
  onSuccess?: () => void;
};

const infoItems = [
  'O cliente tem até 24 horas para responder. Se não responder, a solicitação expira automaticamente.',
  'Após a aprovação do cliente, você tem até 72 horas para iniciar o acesso à conta.',
  'A solicitação pode ser cancelada enquanto aguarda aprovação do cliente.',
  'O acesso expira automaticamente após 30 minutos do início da sessão.',
];

export function DelegatedAccessDialog({
  open,
  title,
  handleClose,
  clientId,
  clientName,
  onSuccess,
}: DialogProps): JSX.Element {
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  const handleSubmit = async (): Promise<void> => {
    if (!clientId) {
      handleSnackbar('Não foi possível identificar o cliente.', true);
      return;
    }

    setLoading(true);
    try {
      const response = await createDelegatedSession({
        client_id: clientId,
        reason: value,
      });

      if (response.detail?.status_code !== StatusCode.OK) {
        const message =
          response.detail?.status_code === StatusCode.PROCESSING
            ? 'Este cliente já possui uma solicitação de acesso em andamento.'
            : response.detail?.description ||
              'Não foi possível enviar a solicitação. Tente novamente.';
        handleSnackbar(message, true);
        return;
      }

      handleSnackbar('Solicitação de acesso delegado enviada!', false);
      setValue('');
      onSuccess?.();
      handleClose();
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContainer>
        <TopContainer>
          <Title>{title}</Title>
          <CloseButton onClick={handleClose}>{IconCloseMS}</CloseButton>
        </TopContainer>

        <WarningMessage>
          <Info fontSize="small" sx={{ color: '#EF9A11' }} />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span className="message-title">
              O acesso requer consentimento explícito do cliente
            </span>
            <span className="message-subtitle">
              A sessão tem duração máxima de 30 minutos após o início do acesso.
            </span>
          </div>
        </WarningMessage>

        <ClientTextField
          label="Cliente"
          value={clientName ?? ''}
          fullWidth
          disabled
        />

        <ReasonBox>
          <ReasonTextField
            label="Motivo da solicitação (visível para o cliente)*"
            multiline
            minRows={5}
            fullWidth
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={`Descreva por que você precisa acessar a conta desse cliente.
Ex.: Cliente reportou erro ao tentar aprovar resultado de cálculo na OS.`}
            inputProps={{
              maxLength: 500,
            }}
          />

          <Caption>{value.length}/500</Caption>
        </ReasonBox>

        <ContainerInfo>
          {infoItems.map((item) => (
            <InfoItem key={item}>{item}</InfoItem>
          ))}
        </ContainerInfo>

        <ContainerButtons>
          <Button
            variant="outlined"
            rounded
            style={{ flex: 1 }}
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Tooltip
            title={`Preencha o campo "Motivo da solicitação" para enviar sua solicitação`}
            disableHoverListener={!!value.length}
            placement="top"
            slotProps={{
              tooltip: {
                sx: {
                  whiteSpace: 'nowrap',
                  maxWidth: 'none',
                },
              },
            }}
          >
            <span style={{ flex: 1, width: '100%' }}>
              <Button
                variant="contained"
                rounded
                disabled={!value.length || loading}
                style={{ width: '100%' }}
                onClick={handleSubmit}
              >
                Enviar solicitação
              </Button>
            </span>
          </Tooltip>
          {/* <div style={{ flex: 1 }}> */}
          {/*  */}
          {/* eslint-disable-next-line */}
          {/* <span style={{ display: 'flex' }}> */}
          {/* </span> */}
          {/* </Tooltip> */}
          {/* </div> */}
        </ContainerButtons>
      </DialogContainer>
    </StyledDialog>
  );
}
